/**
 * Every call the partner portal makes: the backend's `/partner` module
 * (docs/partners.md). Percentages are stored as numeric(5,2), which some
 * drivers serialise as strings ("15.00"); they are coerced to numbers here so
 * the screens can do arithmetic on them.
 */
import { api } from '@/api/client'
import type {
  Commission,
  CommissionStatus,
  Coupon,
  CouponCreate,
  CouponUpdate,
  Dashboard,
  PaginatedResponse,
  Partner,
  PartnerLoginResponse,
  PartnerUpdate,
  Referral,
  Settlement,
} from '@/types/api'

const num = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

const partner = (p: Partner): Partner => ({ ...p, maxDiscountPct: num(p.maxDiscountPct), phone: p.phone ?? '', payoutDetails: p.payoutDetails ?? '', company: p.company ?? '' })

const coupon = (c: Coupon): Coupon => ({
  ...c,
  discountPct: num(c.discountPct),
  commissionPct: num(c.commissionPct),
  redemptions: num(c.redemptions),
  usersOnboarded: num(c.usersOnboarded),
  earnedCents: num(c.earnedCents),
  maxRedemptions: c.maxRedemptions == null ? null : num(c.maxRedemptions),
  note: c.note ?? '',
})

const dashboard = (d: Dashboard): Dashboard => ({
  ...d,
  maxDiscountPct: num(d.maxDiscountPct),
  daily: (d.daily ?? []).map((p) => ({ ...p, usersOnboarded: num(p.usersOnboarded), earnedCents: num(p.earnedCents), paidCents: num(p.paidCents) })),
  topCoupons: (d.topCoupons ?? []).map((c) => ({ ...c, discountPct: num(c.discountPct), commissionPct: num(c.commissionPct), usersOnboarded: num(c.usersOnboarded), earnedCents: num(c.earnedCents) })),
})

const page = <T>(response: PaginatedResponse<T>, map: (row: T) => T): PaginatedResponse<T> => ({ ...response, data: (response.data ?? []).map(map) })

export const authApi = {
  login: async (body: { email: string; password: string }) => {
    const session = await api.postPublic<PartnerLoginResponse>('/partner/auth/login', body)
    return { ...session, partner: partner(session.partner) }
  },
  changePassword: (body: { currentPassword: string; newPassword: string }) => api.post<unknown>('/partner/auth/password', body),
}

export const partnerApi = {
  me: async () => partner(await api.get<Partner>('/partner/me')),
  updateMe: async (body: PartnerUpdate) => partner(await api.patch<Partner>('/partner/me', body)),

  dashboard: async (days: number) => dashboard(await api.get<Dashboard>('/partner/dashboard', { days })),

  coupons: async () => (await api.get<Coupon[]>('/partner/coupons')).map(coupon),
  suggestCode: (word: string) => api.get<{ code: string }>('/partner/coupons/suggest', { word }),
  createCoupon: async (body: CouponCreate) => coupon(await api.post<Coupon>('/partner/coupons', body)),
  updateCoupon: async (id: string, body: CouponUpdate) => coupon(await api.patch<Coupon>(`/partner/coupons/${id}`, body)),
  activateCoupon: async (id: string) => coupon(await api.post<Coupon>(`/partner/coupons/${id}/activate`)),
  deactivateCoupon: async (id: string) => coupon(await api.post<Coupon>(`/partner/coupons/${id}/deactivate`)),
  deleteCoupon: (id: string) => api.delete<void>(`/partner/coupons/${id}`),

  referrals: async (query: { page: number; pageSize: number }) =>
    page(await api.getPage<Referral>('/partner/referrals', query), (r) => ({
      ...r,
      discountPct: num(r.discountPct),
      commissionPct: num(r.commissionPct),
      paidCents: num(r.paidCents),
      earnedCents: num(r.earnedCents),
    })),

  commissions: async (query: { status?: CommissionStatus | ''; page: number; pageSize: number }) =>
    page(await api.getPage<Commission>('/partner/commissions', query), (c) => ({
      ...c,
      grossCents: num(c.grossCents),
      discountCents: num(c.discountCents),
      paidCents: num(c.paidCents),
      commissionPct: num(c.commissionPct),
      commissionCents: num(c.commissionCents),
    })),

  settlements: async () =>
    (await api.get<Settlement[]>('/partner/settlements')).map((s) => ({ ...s, amountCents: num(s.amountCents), commissionsCount: num(s.commissionsCount) })),
}
